/**
 * Event Finder Widget - Embeddable Bundle Entry Point
 *
 * Replaces MP's stock <mpp-event-finder> on woodsidebible.org/events/.
 * Public events only, no 100-row cap, series collapsed to one card, 16:9
 * artwork, and the site's teal. Renders inside a shadow root for style
 * isolation from the WordPress theme (see shell.tsx, shared with the group
 * finder).
 *
 * Embed:
 *   <div id="event-finder-root"></div>
 *   <script src="https://widgets.woodsidebible.org/event-finder/widget.js"></script>
 *
 * Every stock widget attribute works as a data-* attribute or inside the
 * shared `data-params` string, plus the extras. See README.md.
 *
 *   <div id="event-finder-root"
 *        data-params="campus=troy,excludetypes=28,23"
 *        data-details-url="https://woodsidebible.org/events/details/?id="></div>
 */

import React from 'react';
import { EventFinder } from '../src/components/EventFinder';
import { attr, parseDataParams } from './params';
import { ShadowWidget } from './shell';

interface WidgetConfig {
  apiBaseUrl?: string;
  containerId?: string;
  detailsUrl?: string;
}

declare global {
  interface Window {
    EVENT_FINDER_WIDGET_CONFIG?: WidgetConfig;
    EventFinderWidget?: ShadowWidget;
  }
}

const DEFAULT_CONTAINER_ID = 'event-finder-root';

// data-* attributes that are the widget's own, not filter parameters.
const RESERVED = new Set(['params', 'detailsUrl', 'targeturl', 'target']);

function createFinderWidget(config: WidgetConfig): ShadowWidget {
  const apiBaseUrl = config.apiBaseUrl || 'https://api.woodsidebible.org';
  return new ShadowWidget({
    containerIds: config.containerId ? [config.containerId] : [DEFAULT_CONTAINER_ID],
    overlayHostId: 'event-finder-overlay',
    fontId: 'event-finder-widget-montserrat',
    scrollLockFlag: '__EVENT_FINDER_WIDGET_SCROLL_LOCK_PREVENTED__',
    label: 'Event Finder Widget',
    render: (container) => {
      // Precedence: explicit config object > dedicated data-* attributes >
      // the shared data-params string. Any other data-* attribute is a
      // filter parameter (data-ministryid="117", data-hide-filters="1").
      const embedParams = parseDataParams(container.getAttribute('data-params'));
      for (const [key, value] of Object.entries(container.dataset)) {
        if (RESERVED.has(key) || !value?.trim()) continue;
        embedParams.push([key, value.trim()]);
      }
      return (
        <EventFinder
          apiBaseUrl={apiBaseUrl}
          embedParams={embedParams}
          detailsUrl={
            config.detailsUrl ??
            attr(container.dataset.detailsUrl) ??
            attr(container.dataset.targeturl) ??
            attr(container.dataset.target)
          }
        />
      );
    },
  });
}

// Auto-initialize
if (typeof window !== 'undefined') {
  // API base is the centralized api.woodsidebible.org host, baked at build
  // time from CHURCH_HUB_API_URL. (Do NOT derive it from the script origin:
  // the script is served from widgets.woodsidebible.org, which has no /api
  // routes.) window.EVENT_FINDER_WIDGET_CONFIG can override for local testing.
  const config: WidgetConfig = {
    apiBaseUrl: process.env.CHURCH_HUB_API_URL || 'https://api.woodsidebible.org',
    ...(window.EVENT_FINDER_WIDGET_CONFIG || {}),
  };
  window.EVENT_FINDER_WIDGET_CONFIG = config;

  // Skip if already initialized (e.g. script loaded twice via bfcache)
  if (!window.EventFinderWidget) {
    const boot = () => {
      const widget = createFinderWidget(config);
      widget.init();
      window.EventFinderWidget = widget;
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  }
}

export default ShadowWidget;
